import PropTypes from 'prop-types';
import React from 'react';

import BookingModel from '../../../../../models/bookingModel';
import SeatSwapRequest from '../../../../../models/seatSwapRequest';

import { withTranslation } from 'react-i18next';

import { Button, Checkbox, Icon, Header, Loader, Grid, Modal, Segment, Select } from 'semantic-ui-react';

import ManualSeatlibSelection from '../../components/swap/manualSeatlib';

class SwapSeatsAction extends React.Component {
    constructor(props) {
        super(props);

        this.STEPS = {
            swapMode: {
                value: Symbol.for('swapMode'),
                render: () => this.renderSeatsSwapModeSelection()
            },
            zone: {
                value: Symbol.for('zone'),
                render: () => this.renderStepChooseZone()
            },
            zoneConfirmation: {
                value: Symbol.for('zoneConfirmation'),
                render: () => this.renderSeatsConfirmation()
            },
            confirmation: {
                value: Symbol.for('confirmation'),
                render: () => this.renderSeatsConfirmation()
            },
            manualSwap: {
                value: Symbol.for('manualSwap'),
                render: () => this.renderManualSwap()
            },
            success: {
                value: Symbol.for('success'),
                render: () => this.renderSwapSuccess()
            }
        };

        this.state = {
            step: this.STEPS.swapMode,
            show: true,
            selectedZone: null,
            sendEmail: true,
            changeBarcode: false,
            selectedAttendees: this.props.selectedAttendees
        };

        this.checkSeatsCompatibility();
    }

    componentDidUpdate(previousProps) {
        let { isSwappingSeats, isConfirmingSeats, swapError, swapOk } = this.props;

        if (!isSwappingSeats && swapOk && previousProps.isConfirmingSeats && !isConfirmingSeats) {
            this.setState({
                step: this.STEPS.success
            });
        }

        if (!previousProps.swapError && swapError) {
            return this.props.error(swapError);
        }
    }

    /**
     * Check that seats are compatible
     */
    checkSeatsCompatibility() {
        const { t } = this.props;
        let { selectedAttendees } = this.state;

        // Some attendees do not have booking
        if (selectedAttendees.some(attendee => !attendee.booking)) {
            this.props.error(t('attendees.actions.errors.noBooking'));
            return false;
        }

        // Some attendees are placed in GA
        if (selectedAttendees.some(attendee => attendee.booking.isGA)) {
            this.props.error(t('attendees.actions.errors.isGASeated'));
            return false;
        }

        // Some attendees were invited by a partner
        if (selectedAttendees.some(attendee => attendee.partner)) {
            this.props.error(t('attendees.actions.errors.isPartnerInvitation'));
            return false;
        }

        let unicity = selectedAttendees.reduce(
            (acc, attendee) => {
                let booking = attendee.booking;
                acc.categories.push(booking._category);
                acc.contingents.push(booking._contingent);
                acc.inventories.push(booking._inventory);
                return acc;
            },
            {
                contingents: [],
                categories: [],
                inventories: []
            }
        );

        let contingents = new Set(unicity.contingents);
        let categories = new Set(unicity.categories);
        let inventories = new Set(unicity.inventories);

        // Same category
        if (categories.size > 1) {
            this.props.error(t('attendees.actions.errors.categoriesUnicity'));
            return false;
        }

        // Same contingent
        if (contingents.size > 1) {
            this.props.error(t('attendees.actions.errors.contingentsUnicity'));
            return false;
        }

        // Same inventory
        if (inventories.size > 1) {
            this.props.error(t('attendees.actions.errors.inventoriesUnicity'));
            return false;
        }

        return true;
    }

    /**
     * Swap seats
     */
    autoSwapSeats() {
        const { t } = this.props;
        if (!this.state.selectedZone) {
            // something wrong has happened
            return this.props.error(t('attendees.actions.errors.pleaseSelectZone'));
        }

        if (!this.checkSeatsCompatibility()) {
            return;
        }

        // In this case the promise returns the object itself
        this.perform_swap(SeatSwapRequest.automatic.bind(SeatSwapRequest), {
            attendees: this.state.selectedAttendees,
            zone: {
                uuid: this.state.selectedZone.id()
            }
        });
    }

    /**
     * Trigger a manual seat swap
     */
    manualSwapSeats(attendeesMap) {
        if (!this.checkSeatsCompatibility()) {
            return;
        }

        // In this case the promise returns the object itself
        this.perform_swap(SeatSwapRequest.manual.bind(SeatSwapRequest), { attendeesMap });
    }

    perform_swap(SwapRequestType, data) {
        const { t } = this.props;
        SwapRequestType({
            organization: this.props.organization,
            event: this.props.event,
            ...data
        })
            .then(({ swap_request, response }) => {
                let newAttendeesSeats = response.rows.map(row => {
                    let { attendee, old_booking, new_booking } = row;
                    return {
                        attendeeId: attendee,
                        old: new BookingModel(old_booking),
                        new: new BookingModel(new_booking)
                    };
                });

                this.setState({
                    swap_request,
                    step: this.STEPS.confirmation,
                    newAttendeesSeats
                });
            })
            .catch(e => {
                console.error(e);
                this.props.error(t('ticket.attendees.swap.genericError'));
            });
    }

    /**
     * Display manual selection on map
     */
    goToManualSwap() {
        this.setState({
            step: this.STEPS.manualSwap
        });
    }

    /**
     * Display zone selection
     */
    goToZoneSelection() {
        this.setState({
            step: this.STEPS.zone
        });
    }

    /**
     * Confrim manual swap
     */
    manuallySwapSeats() {
        const { t } = this.props;
        let { manuallySelectedSeats, selectedAttendees } = this.state;

        if (!manuallySelectedSeats) {
            this.props.error(t('attendees.actions.errors.selectSeats'));
        }

        if (manuallySelectedSeats.length !== selectedAttendees.length) {
            this.props.error(t('attendees.actions.errors.seatsLength'));
        }

        // perform something
    }

    /**
     * Confirm seats swap
     */
    confirm() {
        const { t } = this.props;

        const { organization, event } = this.props;
        const { sendEmail, changeBarcode, swap_request } = this.state;

        if (!swap_request) {
            throw new Error('[Swap] swap_request is needed to confirm! Possible step misalignment');
        }

        swap_request
            .confirm({
                organization,
                event,
                send_email: sendEmail,
                change_barcode: changeBarcode
            })
            .then(({ response }) => {
                this.setState({
                    step: this.STEPS.success,
                    swap_request: null,
                    newAttendeesSeats: null
                });
            })
            .catch(e => {
                this.props.error(t('ticket.attendees.swap.genericError'));
            });
    }

    /**
     * Hide deletion modal and end action
     */
    hideModal() {
        this.setState(
            {
                show: false
            },
            () => {
                this.props.done();
            }
        );
    }

    /**
     * Triggered when user selects a zone
     */
    onZoneSelect(zoneId) {
        let zone = this.props.zones.find(zone => zone.id() === zoneId);
        this.setState({
            selectedZone: zone
        });
    }

    renderStepChooseZone() {
        const { t } = this.props;
        let text = t('attendees.actionManager.swap.selectZone', {
            count: this.state.selectedAttendees.length
        });

        let inventoryId = this.state.selectedAttendees[0].booking.inventory;
        let categories = Array.from(new Set(this.state.selectedAttendees.map(attendee => attendee.booking.category)));

        let zones = this.props.zones.filter(zone => {
            // If zone has no seat with categories on this inventory
            // it cannot be selected for seat swapping
            if (!zone.categories(inventoryId).length) {
                return false;
            }

            return categories.some(category => zone.categories(inventoryId).includes(category));
        });

        let options = zones
            .sort((a, b) => {
                // sort alphabetically
                return a.name() - b.name();
            })
            .map(zone => ({
                text: zone.name(),
                value: zone.id()
            }));

        let defaultZone = options.find(({ value }) => value === this.state.selectedAttendees[0].booking.zone);

        if (!this.state.selectedZone) {
            this.setState({
                selectedZone: zones.find(zone => zone.id() === defaultZone.value)
            });
        }

        return (
            <React.Fragment>
                <Header icon="map signs" content={text} />
                <Segment basic>
                    <Select
                        search={true}
                        fluid
                        defaultSelectedLabel={defaultZone.text}
                        defaultValue={defaultZone.value}
                        options={options}
                        onChange={(synthetic, { value }) => this.onZoneSelect(value)}
                    />
                </Segment>
                <Modal.Actions>
                    <Button basic color="red" inverted onClick={this.hideModal.bind(this)}>
                        <Icon name="remove" /> {t('ticket.common.action.cancel')}
                    </Button>
                    <Button color="green" inverted onClick={this.autoSwapSeats.bind(this)}>
                        <Icon name="cogs" /> {t('attendees.actions.automatically')}
                    </Button>
                </Modal.Actions>
            </React.Fragment>
        );
    }

    renderSeatsSwapModeSelection() {
        const { t } = this.props;
        let text = t('attendees.actionManager.swap.confirmation', {
            count: this.state.selectedAttendees.length
        });

        return (
            <React.Fragment>
                <Header content={text} />
                <Modal.Actions>
                    <Button basic color="red" inverted onClick={this.hideModal.bind(this)}>
                        <Icon name="remove" /> {t('ticket.common.action.cancel')}
                    </Button>
                    <Button basic color="green" inverted onClick={this.goToManualSwap.bind(this)}>
                        <Icon name="hand pointer outline" /> {t('attendees.actions.manually')}
                    </Button>
                    <Button color="green" inverted onClick={this.goToZoneSelection.bind(this)}>
                        <Icon name="cogs" /> {t('attendees.actions.automatically')}
                    </Button>
                </Modal.Actions>
            </React.Fragment>
        );
    }

    renderManualSwap() {
        return (
            <ManualSeatlibSelection
                onCancel={() => this.setState({ step: this.STEPS.swapMode })}
                onSubmit={attendeesMap => this.manualSwapSeats(attendeesMap)}
                connectData={this.props.connectData}
                neededSeats={this.state.selectedAttendees.length}
                organizationId={this.props.organizationId}
                event={this.props.event}
                organization={this.props.organization}
                selectedAttendees={this.state.selectedAttendees}
                setSelectedAttendees={selectedAttendees => this.setState({ selectedAttendees })}
                pyvarUrl={this.props.apiUrl}
                rates={this.props.rates}
            />
        );
    }

    renderSwapSuccess() {
        const { t } = this.props;
        return (
            <React.Fragment>
                <Header content={t('attendees.actionManager.swap.success')} />
                <Modal.Actions>
                    <Button color="green" inverted onClick={this.hideModal.bind(this)}>
                        <Icon name="checkmark" /> OK
                    </Button>
                </Modal.Actions>
            </React.Fragment>
        );
    }

    renderSeatsConfirmation() {
        const { t } = this.props;
        // show seats
        let text = t('attendees.actionManager.swap.confirmSeats');
        return (
            <React.Fragment>
                <Header content={text} />
                <Modal.Content>
                    <Grid stackable>
                        <Grid.Column width={10}>
                            <ul>
                                {this.state.newAttendeesSeats.map((newSeat, index) => {
                                    let attendee = this.state.selectedAttendees.find(sattendee => sattendee.pk === newSeat.attendeeId);
                                    let oldBooking = newSeat.old;
                                    let newBooking = newSeat.new;
                                    return (
                                        <li key={index}>
                                            <div>{`${attendee.first_name} ${attendee.last_name} (#${attendee.pk}):`}</div>
                                            <div>
                                                <ul>
                                                    <li>
                                                        {`${t('ticket.overview.dividerFrom')}`}
                                                        <br />
                                                        {`Zone: ${oldBooking.zoneName}`}
                                                        <br />
                                                        {`${oldBooking.label} Access: ${oldBooking.access}`}
                                                    </li>
                                                    <li>
                                                        {`${t('ticket.overview.dividerTo')}`}
                                                        <br />
                                                        {`Zone: ${newBooking.zoneName}`}
                                                        <br />
                                                        {`${newBooking.label} Access: ${newBooking.access}`}
                                                    </li>
                                                </ul>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </Grid.Column>
                        <Grid.Column width={6}>
                            <Header inverted as="h3">
                                {t('attendees.actions.swapConfirmationOptions')}
                            </Header>
                            <div className="inverted-checkbox">
                                <Checkbox
                                    inverted
                                    label={t('attendees.actions.swapSendEmail')}
                                    checked={this.state.sendEmail}
                                    onChange={(synth, { checked }) => this.setState({ sendEmail: checked })}
                                />
                            </div>
                            <div className="inverted-checkbox">
                                <Checkbox inverted label={t('attendees.actions.swapChangeBarcode')} onChange={(synth, { checked }) => this.setState({ changeBarcode: checked })} />
                            </div>
                        </Grid.Column>
                    </Grid>
                </Modal.Content>
                <Modal.Actions>
                    <Button basic color="red" inverted onClick={this.hideModal.bind(this)}>
                        <Icon name="remove" /> {t('ticket.common.action.back')}
                    </Button>
                    <Button color="green" inverted onClick={this.confirm.bind(this)}>
                        <Icon name="cogs" /> {t('ticket.common.action.confirm')}
                    </Button>
                </Modal.Actions>
            </React.Fragment>
        );
    }

    render() {
        let render = null;

        if (!this.props.zones) {
            render = <Loader />;
        } else {
            let { step } = this.state;
            render = step.render();
        }
        return (
            <Modal open={this.state.show} basic size="small" className="action-modal">
                {render}
            </Modal>
        );
    }
}

SwapSeatsAction.defaultProps = {
    done: () => {},
    error: () => {},
    swapError: '',
    swapOk: false,
    newAttendeesSeats: [],
    isConfirmingSeats: false
};

SwapSeatsAction.propTypes = {
    organization: PropTypes.object.isRequired,
    done: PropTypes.func,
    error: PropTypes.func,
    event: PropTypes.object.isRequired,
    connectData: PropTypes.object.isRequired,
    isConfirmingSeats: PropTypes.bool.isRequired,
    isSwappingSeats: PropTypes.bool.isRequired,
    newAttendeesSeats: PropTypes.array,
    organizationId: PropTypes.string.isRequired,
    apiUrl: PropTypes.string.isRequired,
    selectedAttendees: PropTypes.array.isRequired,
    swapError: PropTypes.string,
    swapOk: PropTypes.bool,
    swapSeatsRequestId: PropTypes.number,
    zones: PropTypes.array,
    rates: PropTypes.array,
    t: PropTypes.func.isRequired
};

export default withTranslation()(SwapSeatsAction);
